import { create } from 'zustand'
import type { SparePart, PartCategory, PartBrand } from '@/types'
import { usePartsState } from './usePartsState'

export type PartSortMode = 'featured' | 'price-asc' | 'price-desc' | 'name'

interface PartFilterState {
  selectedCategories: PartCategory[]
  selectedBrands: PartBrand[]
  searchText: string
  minPrice: number
  maxPrice: number
  filteredParts: SparePart[]
  isSearchActive: boolean
  loading: boolean
  setSelectedCategories: (categories: PartCategory[]) => void
  setSelectedBrands: (brands: PartBrand[]) => void
  setSearchText: (searchText: string) => void
  setMinPrice: (minPrice: number) => void
  setMaxPrice: (maxPrice: number) => void
  updateFilters: () => void
  setFilteredParts: (parts: SparePart[]) => void
}

const initialState = {
  selectedCategories: [] as PartCategory[],
  selectedBrands: [] as PartBrand[],
  searchText: '',
  minPrice: 0,
  maxPrice: 0,
  filteredParts: [] as SparePart[],
  isSearchActive: false,
  loading: false,
}

export const usePartFilter = create<PartFilterState>((set, get) => ({
  ...initialState,

  updateFilters: () => {
    const state = get()
    const { parts } = usePartsState.getState()
    if (!parts) return

    const isActive =
      state.searchText.trim() !== '' ||
      state.selectedBrands.length > 0 ||
      state.selectedCategories.length > 0 ||
      state.minPrice > 0 ||
      state.maxPrice > 0

    const searchLower = (state.searchText || '').toLowerCase()
    const filtered = parts.filter((part) => {
      if (!part) return false
      const name = (part.name || '').toLowerCase()
      const sku = (part.sku || '').toLowerCase()
      const brandName = (part.brand?.name || '').toLowerCase()
      const categoryName = (part.category?.name || '').toLowerCase()
      const matchesSearch =
        searchLower === '' ||
        name.includes(searchLower) ||
        sku.includes(searchLower) ||
        brandName.includes(searchLower) ||
        categoryName.includes(searchLower)
      const matchesBrand =
        state.selectedBrands.length === 0 ||
        state.selectedBrands.some((b) => part.brand?.id === b.id)
      const matchesCategory =
        state.selectedCategories.length === 0 ||
        state.selectedCategories.some((c) => part.category?.id === c.id)
      const price = part.price ?? 0
      const matchesMin = state.minPrice <= 0 || price >= state.minPrice
      const matchesMax = state.maxPrice <= 0 || price <= state.maxPrice
      return matchesSearch && matchesBrand && matchesCategory && matchesMin && matchesMax
    })

    set({
      filteredParts: filtered,
      isSearchActive: isActive,
      loading: false,
    })
  },

  setMinPrice: (minPrice) => {
    set({ minPrice })
    get().updateFilters()
  },
  setMaxPrice: (maxPrice) => {
    set({ maxPrice })
    get().updateFilters()
  },
  setSelectedCategories: (categories) => {
    set({ selectedCategories: categories })
    get().updateFilters()
  },
  setSelectedBrands: (brands) => {
    set({ selectedBrands: brands })
    get().updateFilters()
  },
  setSearchText: (searchText) => {
    set({ searchText })
    get().updateFilters()
  },
  setFilteredParts: (filteredParts) => set({ filteredParts }),
}))
